import { FoodsSelect, FoodsTextInput, FormBox, Textarea, Typography } from "@foodpilot/foods";
import type { CsrPillarForm } from "../PillarForm.utils";
import { ButtonBase, Stack, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { csrPillarIconKeys, getCsrPillarIconKey } from "src/utils/csrStrategyPillarIcons";
import { noop } from "src/utils/noop";
import { CsrPillarIcon } from "src/components/CsrPillarIcon";

type PillarInfoProps = {
    form: CsrPillarForm;
    setForm: (form: CsrPillarForm) => void;
    dialogLayout?: boolean;
};
export const PillarInfo = (props: PillarInfoProps) => {
    const { form, setForm, dialogLayout = false } = props;

    const theme = useTheme();
    const { t } = useTranslation();

    const fields = (
        <Stack gap={theme.spacing(dialogLayout ? 3 : 2)} padding={dialogLayout ? { xs: 3, md: 4 } : undefined}>
            <Stack>
                <Typography variant="big-medium">{t("settings.csr.pillars.fields.name.label") + "*"}</Typography>
                {dialogLayout ?
                    <Textarea
                        value={form.name}
                        onChange={(newValue) => setForm({ ...form, name: newValue })}
                        minRows={3}
                        placeholder={t("settings.csr.pillars.fields.name.placeholder")}
                    />
                :   <FoodsTextInput
                        value={form.name}
                        onChange={(newValue) => setForm({ ...form, name: newValue })}
                        ExtraInputProps={{ placeholder: t("settings.csr.pillars.fields.name.placeholder") }}
                    />
                }
            </Stack>

            {!dialogLayout && (
                <Stack>
                    <Typography variant="big-medium">{t("settings.csr.pillars.fields.themes.label")}</Typography>
                    <FoodsSelect
                        selectedOption={null}
                        options={[{ id: 0, name: "" }]}
                        onChange={noop}
                        disabled
                        getId={(item) => item.id}
                        getName={(item) => item.name}
                        textOptions={{
                            placeholder: t("settings.csr.pillars.fields.themes.placeholder"),
                            noResult: "",
                            searchPlaceholder: "",
                            emptyOptionsText: t("select.generic.no_options"),
                        }}
                    />
                </Stack>
            )}

            <Stack>
                <Typography variant="big-medium">
                    {t("settings.csr.pillars.fields.icon") + (dialogLayout ? "*" : "")}
                </Typography>
                <Stack flexDirection="row" flexWrap="wrap" gap={theme.spacing(2)}>
                    {csrPillarIconKeys.map((iconName) => {
                        const isSelected = iconName === getCsrPillarIconKey(form.icon);
                        return (
                            <ButtonBase
                                key={iconName}
                                data-testid={`pillar-info-icon-${iconName}`}
                                aria-label={iconName}
                                aria-pressed={isSelected}
                                sx={{
                                    display: "flex",
                                    width: 54,
                                    height: 54,
                                    borderRadius: "50%",
                                    border: isSelected ? `2px solid ${theme.palette.primary.main}` : "none",
                                }}
                                onClick={() => {
                                    setForm({
                                        ...form,
                                        icon: iconName,
                                    });
                                }}
                            >
                                <CsrPillarIcon icon={iconName} backgroundShape="circle" height={50} />
                            </ButtonBase>
                        );
                    })}
                </Stack>
            </Stack>
        </Stack>
    );

    return dialogLayout ? fields : <FormBox>{fields}</FormBox>;
};
